import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box, Card, Stack, Typography, Input, CircularProgress, Button, Chip, Switch, Alert,
  Table, Sheet, Modal, ModalDialog, ModalClose, Select, Option, IconButton,
} from '@mui/joy';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  APIFinancePaymentCalendar, APIFinanceRecurring, APIFinanceRecurringCreate, APIFinanceRecurringUpdate, APIFinanceRecurringDelete,
} from '../../../api';
import {
  useFinanceToken, useIsMobile, MetricCard, MetricGrid, SectionCard, scrollTableSx, fmtMoney,
} from './financeShared';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '');
const KIND_LABELS = { receivable: 'Поступление', payable: 'Выплата исп.', recurring: 'Регулярный' };
const KIND_COLOR = { receivable: 'success', payable: 'warning', recurring: 'primary' };

// Точка линии баланса: красная в дни кассового разрыва
const GapDot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  if (payload?.is_gap) return <circle cx={cx} cy={cy} r={4} fill="#ef4444" stroke="#fff" strokeWidth={1} />;
  return <circle cx={cx} cy={cy} r={2.5} fill="#6366f1" />;
};

export default function FinancePaymentCalendarTab() {
  const token = useFinanceToken();
  const isMobile = useIsMobile();
  const [weeks, setWeeks] = useState(4);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    APIFinancePaymentCalendar({ weeks }, token)
      .then(setData).catch((e) => console.error('calendar', e)).finally(() => setLoading(false));
  }, [token, weeks]);
  useEffect(() => { load(); }, [load]);

  // ── Пагинация списка ожидаемых движений: по 30, догрузка при скролле ──
  const [visibleCount, setVisibleCount] = useState(30);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  // «свежие сверху» = ближайшие по дате первыми (для обратного порядка поменяйте на b.date/a.date)
  const sortedItems = useMemo(
    () => [...(data?.items || [])].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    [data]
  );
  const shownItems = sortedItems.slice(0, visibleCount);

  useEffect(() => { setVisibleCount(30); }, [data]);

  useEffect(() => {
    const root = scrollRef.current;
    const el = sentinelRef.current;
    if (!root || !el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setVisibleCount((c) => (c < sortedItems.length ? c + 30 : c)); },
      { root, rootMargin: '120px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [sortedItems.length, shownItems.length]);

  const daily = data?.daily || [];
  const endBalance = daily.length ? daily[daily.length - 1].balance : data?.start_balance;

  return (
    <Box>
      {/* Тулбар: горизонт прогноза + регулярные платежи */}
      <Card variant="soft" color="neutral" sx={{ p: 1, mb: 2, borderRadius: 'xl', bgcolor: 'background.level1', boxShadow: 'none' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="body-sm" sx={{ whiteSpace: 'nowrap' }}>Горизонт:</Typography>
            <Select size="sm" value={weeks} onChange={(_, v) => v && setWeeks(v)} sx={{ minWidth: 130 }}>
              <Option value={2}>2 недели</Option>
              <Option value={3}>3 недели</Option>
              <Option value={4}>4 недели</Option>
              <Option value={6}>6 недель</Option>
            </Select>
            {loading && <CircularProgress size="sm" />}
          </Stack>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          <Button size="sm" variant="outlined" color="neutral" startDecorator={<EventRepeatRoundedIcon />} onClick={() => setRecurringOpen(true)} sx={{ borderRadius: 'lg' }}>
            Регулярные платежи
          </Button>
        </Stack>
      </Card>

      {data?.has_cash_gap && (
        <Alert
          color="danger" variant="soft" startDecorator={<WarningRoundedIcon />}
          sx={{ mb: 2, borderRadius: 'xl', alignItems: 'flex-start' }}
        >
          <Box>
            <Typography level="title-sm" sx={{ fontWeight: 700, color: 'inherit' }}>Прогнозируется кассовый разрыв</Typography>
            <Typography level="body-sm" sx={{ color: 'inherit' }}>
              {(data.gap_dates || []).map(fmtDate).join(', ')} — баланс уходит в минус. Спланируйте поступления или отсрочки платежей.
            </Typography>
          </Box>
        </Alert>
      )}

      <MetricGrid>
        <MetricCard index={0} title="Баланс сейчас" value={fmtMoney(data?.start_balance)} color="#6366f1" />
        <MetricCard index={1} title="Ожидается приход" value={fmtMoney(data?.totals?.expected_in)} color="#059669" />
        <MetricCard index={2} title="Ожидается расход" value={fmtMoney(data?.totals?.expected_out)} color="#f59e0b" />
        <MetricCard index={3} title="Прогноз на конец" value={fmtMoney(endBalance)} color={endBalance < 0 ? '#ef4444' : '#0ea5e9'} />
      </MetricGrid>

      <SectionCard title="Прогноз баланса по дням">
        <Box sx={{ height: { xs: 260, md: 320 }, mx: { xs: -0.5, md: 0 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={daily.map((d) => ({ ...d, label: fmtDate(d.date) }))} margin={{ top: 8, right: 12, left: isMobile ? -18 : 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
              <YAxis tick={{ fontSize: 11 }} width={isMobile ? 46 : 64} tickFormatter={(v) => Number(v).toLocaleString('ru-RU')} />
              <RTooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
              <Legend />
              <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 4" />
              <Bar dataKey="in"  name="Приход" fill="#05966955" radius={[3, 3, 0, 0]} />
              <Bar dataKey="out" name="Расход" fill="#f59e0b55" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="balance" name="Баланс" stroke="#6366f1" strokeWidth={2} dot={<GapDot />} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </SectionCard>

      <SectionCard title="Ожидаемые движения">
        <Sheet ref={scrollRef} variant="outlined" sx={{ borderRadius: 'lg', maxHeight: 520, overflow: 'auto' }}>
          <Table borderAxis="both" stickyHeader hoverRow size="sm" sx={scrollTableSx(560)}>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Дата</th>
                <th style={{ width: 150 }}>Тип</th>
                <th>Назначение</th>
                <th style={{ width: 140, textAlign: 'right' }}>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {shownItems.map((it, i) => (
                <tr key={i}>
                  <td>{fmtDate(it.date)}</td>
                  <td><Chip size="sm" variant="soft" color={KIND_COLOR[it.kind] || 'neutral'}>{KIND_LABELS[it.kind] || it.kind}</Chip></td>
                  <td>{it.title}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: it.direction === 'in' ? '#059669' : '#b45309' }}>
                    {it.direction === 'in' ? '+' : '−'}{fmtMoney(it.amount)}
                  </td>
                </tr>
              ))}
              {sortedItems.length === 0 && !loading && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>Нет ожидаемых движений в горизонте</td></tr>
              )}
            </tbody>
          </Table>
          <Box ref={sentinelRef} sx={{ height: 1 }} />
        </Sheet>
        {sortedItems.length > 0 && (
          <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', mt: 1 }}>
            Показано {shownItems.length} из {sortedItems.length}
          </Typography>
        )}
      </SectionCard>

      <RecurringModal open={recurringOpen} token={token} onClose={() => setRecurringOpen(false)} onChanged={load} />
    </Box>
  );
}

// ── Модалка регулярных обязательств ──
function RecurringModal({ open, token, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState(1);
  const [freq, setFreq] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();

  const loadList = () => {
    if (!token) return;
    APIFinanceRecurring({}, token).then((d) => setList(d || [])).catch(() => {});
  };
  useEffect(() => { if (open) loadList(); }, [open, token]);

  const add = () => {
    if (!name.trim() || !amount) return;
    setBusy(true);
    APIFinanceRecurringCreate({ name: name.trim(), amount: Number(amount), day_of_month: Number(day), frequency: freq }, token)
      .then(() => { setName(''); setAmount(''); loadList(); onChanged?.(); })
      .catch((e) => console.error(e)).finally(() => setBusy(false));
  };
  const toggle = (ob) => {
    APIFinanceRecurringUpdate(ob.id, { is_active: !ob.is_active }, token)
      .then(() => { loadList(); onChanged?.(); }).catch((e) => console.error(e));
  };
  const remove = (ob) => {
    if (!window.confirm(`Удалить «${ob.name}»?`)) return;
    APIFinanceRecurringDelete(ob.id, token)
      .then(() => { loadList(); onChanged?.(); }).catch((e) => console.error(e));
  };

  return (
    <Modal open={open} onClose={onClose}>
        <ModalDialog
          layout={isMobile ? 'fullscreen' : 'center'}
          sx={{
            width: { xs: '100%', sm: 560 }, borderRadius: { xs: 0, sm: 'xl' },
            maxHeight: { xs: '100%', sm: '90dvh' }, overflowY: 'auto',
          }}
        >
          <ModalClose onClick={onClose} />
          <Typography level="title-md" sx={{ mb: 0.5 }}>Регулярные обязательства</Typography>
          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Аренда, налоги, подписки — попадают в платёжный календарь как ожидаемые платежи.
          </Typography>

          <Sheet variant="soft" sx={{ borderRadius: 'sm', mb: 1.5, maxHeight: 240, overflow: 'auto' }}>
            <Table size="sm">
              <thead>
                <tr>
                  <th>Название</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Сумма</th>
                  <th style={{ width: 70, textAlign: 'center' }}>День</th>
                  <th style={{ width: 90, textAlign: 'center' }}>Активно</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {list.map((ob) => (
                  <tr key={ob.id}>
                    <td>{ob.name}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(ob.amount)}</td>
                    <td style={{ textAlign: 'center' }}>{ob.day_of_month}</td>
                    <td style={{ textAlign: 'center' }}><Switch size="sm" checked={ob.is_active} onChange={() => toggle(ob)} /></td>
                    <td style={{ textAlign: 'center' }}>
                      <IconButton size="sm" variant="plain" color="danger" onClick={() => remove(ob)}><DeleteRoundedIcon fontSize="small" /></IconButton>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>Пока нет</td></tr>}
              </tbody>
            </Table>
          </Sheet>

          <Typography level="title-sm" sx={{ mb: 0.5 }}>Добавить</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Input size="sm" placeholder="Название" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: '1 1 160px' }} />
            <Input size="sm" type="number" placeholder="Сумма" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ width: 110 }} />
            <Input size="sm" type="number" placeholder="День" value={day} onChange={(e) => setDay(e.target.value)} sx={{ width: 80 }} slotProps={{ input: { min: 1, max: 28 } }} />
            <Select size="sm" value={freq} onChange={(_, v) => v && setFreq(v)} sx={{ width: 150 }}>
              <Option value="monthly">Ежемесячно</Option>
              <Option value="quarterly">Раз в квартал</Option>
            </Select>
            <Button size="sm" startDecorator={<AddRoundedIcon />} onClick={add} loading={busy}>Добавить</Button>
          </Stack>
        </ModalDialog>
    </Modal>
  );
}